import { Namespace, TFunction, ParseKeys } from 'i18next';
import {
  practitionerCountriesQuery,
  practitionerCountriesQuery_practitionerCountries,
} from 'Services/BeGateway/Practitioner/__generated__/practitionerCountriesQuery';
import { PRACTITIONER_COUNTRIES_QUERY } from 'Services/BeGateway/Practitioner/practitioner.services';
import { useQuery } from '@apollo/client';
import { BEGatewayQueryContext } from 'utils/apollo';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { states } from 'Book/shared/data';

const getCountryLabel = (
  country: practitionerCountriesQuery_practitionerCountries,
  translateCountry: TFunction<Namespace<'country'>>,
) => {
  if (country?.isoCodeTwo) {
    return translateCountry(
      country?.isoCodeTwo as ParseKeys<Namespace<'country'>>,
    );
  }

  if (country?.name) {
    return country?.name;
  }

  return '';
};

const countryData = (
  translateCountry: TFunction<Namespace<'country'>>,
  countries?: practitionerCountriesQuery['practitionerCountries'],
) => {
  const countryOptions =
    countries && countries?.length > 0
      ? countries?.map(country => ({
          value: country?.name ?? '',
          label: getCountryLabel(country, translateCountry),
        }))
      : [];
  countryOptions.sort((a, b) => a.label.localeCompare(b.label));

  return countryOptions;
};

export const getStateData = (country: keyof typeof states) =>
  states[country] && states[country].length > 0
    ? states[country].map(state => ({
        label: state.label,
        value: state.value,
      }))
    : [];

export const usePractitionerCountries = () => {
  const { t: translateCountry } =
    useTranslation<Namespace<'country'>>('country');

  const { data: countriesQueryData, loading } =
    useQuery<practitionerCountriesQuery>(PRACTITIONER_COUNTRIES_QUERY, {
      ...BEGatewayQueryContext,
    });
  const countries = countriesQueryData?.practitionerCountries;
  const countryOptions = useMemo(
    () => countryData(translateCountry, countries),
    [countries, translateCountry],
  );

  return {
    loading,
    countryData: countryOptions,
    getStateData,
  };
};
